import React, { useState, useEffect, useRef, useCallback } from "react";
import { Bell, X, Check, AlertTriangle, Info, Trash2 } from "lucide-react";
import safeStorage from "../lib/safeStorage";
import { Button } from "./ui/button";
import { tx as __tx } from "../lib/tx";

const MAX_NOTIFICATIONS = 50;

let globalListener = null;

export function pushNotification(type, message) {
  if (globalListener) globalListener({ type, message, id: Date.now(), time: new Date() });
}

export default function NotificationCenter() {
  const [notifications, setNotifications] = useState(() =>
    safeStorage.getJSON("xpnsr_notifications", []),
  );
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const addNotification = useCallback((n) => {
    setNotifications((prev) => {
      const next = [n, ...prev].slice(0, MAX_NOTIFICATIONS);
      safeStorage.setJSON("xpnsr_notifications", next);
      return next;
    });
  }, []);

  useEffect(() => {
    globalListener = addNotification;
    return () => { globalListener = null; };
  }, [addNotification]);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const clearAll = () => {
    setNotifications([]);
    safeStorage.removeItem("xpnsr_notifications");
  };

  const removeOne = (id) => {
    setNotifications((prev) => {
      const next = prev.filter((n) => n.id !== id);
      safeStorage.setJSON("xpnsr_notifications", next);
      return next;
    });
  };

  const unread = notifications.length;
  const icon = { success: Check, error: AlertTriangle, info: Info, warning: AlertTriangle };

  const formatTime = (t) => {
    const d = new Date(t);
    const now = new Date();
    const diff = (now - d) / 1000;
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return d.toLocaleDateString();
  };

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="relative p-2 rounded-lg bg-transparent border-none text-muted hover:text-text cursor-pointer transition-colors"
        data-testid="notification-bell"
        aria-label={unread > 0 ? `Notifications (${unread} unread)` : 'Notifications'}
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        <Bell size={18} aria-hidden="true" />
        {unread > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 w-4 h-4 rounded-full bg-seo text-white text-[10px] font-bold flex items-center justify-center"
            aria-hidden="true"
          >
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 top-full mt-2 w-80 max-h-96 rounded-xl border border-border bg-surface shadow-2xl z-50 overflow-hidden"
          style={{ background: "rgb(var(--surface))" }}
          role="dialog"
          aria-label={__tx("Notifications panel")}
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-border">
            <span className="font-semibold text-sm" style={{ color: "rgb(var(--text))" }}>
              Notifications ({unread})
            </span>
            {unread > 0 && (
              <Button variant="ghost" size="sm" onClick={clearAll} className="h-7 px-2 text-xs">
                <Trash2 size={12} className="mr-1" /> {__tx("Clear all")}
              </Button>
            )}
          </div>
          <div className="overflow-y-auto max-h-80">
            {notifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 text-muted">
                <Bell size={24} className="mb-2 opacity-30" />
                <span className="text-xs">{__tx("No notifications")}</span>
              </div>
            ) : (
              notifications.map((n) => {
                const Icon = icon[n.type] || Info;
                const color = n.type === "error" ? "text-red-400" : n.type === "success" ? "text-green-400" : n.type === "warning" ? "text-yellow-400" : "text-blue-400";
                return (
                  <div key={n.id} className="flex items-start gap-3 px-4 py-3 border-b border-border/50 hover:bg-surface-2/50 transition-colors group">
                    <Icon size={14} className={`mt-0.5 shrink-0 ${color}`} />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs leading-relaxed" style={{ color: "rgb(var(--text))" }}>{n.message}</p>
                      <span className="text-[10px] text-muted">{formatTime(n.time)}</span>
                    </div>
                    <button
                      onClick={() => removeOne(n.id)}
                      className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-surface-2 transition-all border-none bg-transparent cursor-pointer"
                      aria-label={__tx("Dismiss notification")}
                    >
                      <X size={12} className="text-muted" aria-hidden="true" />
                    </button>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
